import React, { useEffect, useMemo, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  fetchBuyerOrders,
  formatOrderAmount,
} from '../api/orderApi'
import { resolveShopUserId } from '../api/shopAuth'
import RelatedProductsSection from '../components/UI/RelatedProductsSection'

function formatOrderDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return String(value).replace('T', ' ').slice(0, 16)
  }
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function OrderHistoryPage() {
  const navigate = useNavigate()
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [authenticated, setAuthenticated] = useState(true)

  const excludeIds = useMemo(
    () =>
      orders
        .map((order) => order.asset_id)
        .filter((id) => id != null && id !== ''),
    [orders]
  )

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const userId = await resolveShopUserId()
        if (cancelled) return

        if (userId == null) {
          setAuthenticated(false)
          setOrders([])
          return
        }

        setAuthenticated(true)
        const rows = await fetchBuyerOrders(userId)
        if (!cancelled) setOrders(rows)
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load order history')
          setOrders([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  if (!authenticated && !loading) {
    return (
      <section className="space-top">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="text-lora font-semibold mb-3">Order History</h2>
            <p className="light-color mb-4">
              Please log in to view the orders you have placed.
            </p>
            <NavLink
              to="/login"
              state={{ from: '/order-history' }}
              className="first-btn d-inline-block"
              style={{ width: 'auto', padding: '14px 28px' }}
            >
              Login
            </NavLink>
          </div>
          <RelatedProductsSection />
        </div>
      </section>
    )
  }

  if (loading) {
    return (
      <section className="space-top">
        <div className="container py-5">
          <div className="text-center mb-5">
            <h2 className="text-lora font-semibold mb-3">Order History</h2>
            <p className="light-color">Loading your orders...</p>
          </div>
          <RelatedProductsSection />
        </div>
      </section>
    )
  }

  return (
    <section className="space-top">
      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-end mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-lora font-semibold mb-1">Order History</h2>
            <p className="light-color mb-0">
              Orders you have placed from the shop.
            </p>
          </div>
          <NavLink
            to="/shop"
            className="first-btn d-inline-block"
            style={{ width: 'auto', padding: '12px 22px' }}
          >
            Continue Shopping
          </NavLink>
        </div>

        {error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : null}

        {orders.length === 0 && !error ? (
          <div className="text-center py-5">
            <p className="light-color mb-4">
              You have not placed any orders yet.
            </p>
            <button
              type="button"
              className="first-btn"
              style={{ width: 'auto', padding: '14px 28px' }}
              onClick={() => navigate('/shop')}
            >
              Browse Shop
            </button>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {orders.map((order) => {
              const title =
                order.service_title ||
                (order.asset_id ? `Product #${order.asset_id}` : 'Order item')
              const sellerName = [order.seller_first_name, order.seller_last_name]
                .filter(Boolean)
                .join(' ')
              const currency = order.currency || 'INR'
              const amountLabel =
                order.amount != null && order.amount !== ''
                  ? `${currency === 'INR' ? '₹' : `${currency} `}${formatOrderAmount(
                      order.amount
                    )}`
                  : '—'

              return (
                <article
                  key={order.order_id}
                  className="border rounded-3 p-3 p-md-4 bg-white"
                >
                  <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                    <div>
                      <h3 className="h6 text-lora font-semibold mb-1">
                        {title}
                      </h3>
                      <p className="small text-muted mb-0">
                        Order #{order.order_id}
                        {order.order_status_name
                          ? ` · ${order.order_status_name}`
                          : ''}
                      </p>
                    </div>
                    <div className="text-md-end">
                      <div className="fw-semibold">{amountLabel}</div>
                      <div className="small text-muted">
                        {formatOrderDate(order.order_datetime)}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-3 small text-muted">
                    <span>Qty: {order.quantity ?? 1}</span>
                    {sellerName ? <span>Seller: {sellerName}</span> : null}
                    {order.unit_price != null && order.unit_price !== '' ? (
                      <span>
                        {/* Discount :{' '}
                        {currency === 'INR' ? '₹' : `${currency} `}
                        {formatOrderAmount(order.unit_price)} */}
                      </span>
                    ) : null}
                  </div>
                </article>
              )
            })}
          </div>
        )}

        <RelatedProductsSection excludeIds={excludeIds} />
      </div>
    </section>
  )
}
